import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

function TransactionsTable({ transactions }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getPaymentMethodColor = (method) => {
    return method === 'card' ? 'info' : 'success';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
    };
    return colors[status] || 'default';
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'booking_date',
        header: 'Date',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={500}>
            {getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'start_time',
        header: 'Time',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
      },
      {
        accessorKey: 'customer_name',
        header: 'Customer',
        cell: ({ getValue, row }) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {getValue()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.customer_email}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'service_name',
        header: 'Service',
        cell: ({ getValue }) => (
          <Typography variant="body2">{getValue()}</Typography>
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ getValue }) => (
          <Typography variant="body2" fontWeight={600}>
            £{getValue().toFixed(2)}
          </Typography>
        ),
      },
      {
        accessorKey: 'payment_method',
        header: 'Payment Method',
        cell: ({ getValue }) => (
          <Chip
            label={getValue()}
            color={getPaymentMethodColor(getValue())}
            size="small"
            sx={{ fontWeight: 500, textTransform: 'capitalize' }}
          />
        ),
      },
      {
        accessorKey: 'payment_status',
        header: 'Status',
        cell: ({ getValue }) => (
          <Chip
            label={getValue()}
            color={getPaymentStatusColor(getValue())}
            size="small"
            sx={{ fontWeight: 500, textTransform: 'capitalize' }}
          />
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (transactions.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'info.light',
          borderRadius: 2,
          color: 'info.dark',
        }}
      >
        <Typography variant="body1">No transactions found for the selected filters.</Typography>
      </Box>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {transactions.map((transaction) => (
          <Paper
            key={transaction.id}
            elevation={2}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.12), 0 4px 8px -2px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {transaction.booking_date}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {transaction.start_time}
                </Typography>
              </Box>
              <Chip
                label={transaction.payment_status}
                color={getPaymentStatusColor(transaction.payment_status)}
                size="small"
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Customer
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {transaction.customer_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {transaction.customer_email}
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Service
              </Typography>
              <Typography variant="body2">{transaction.service_name}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Amount
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  £{transaction.amount.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Payment Method
                </Typography>
                <Chip
                  label={transaction.payment_method}
                  color={getPaymentMethodColor(transaction.payment_method)}
                  size="small"
                  sx={{ mt: 0.5, textTransform: 'capitalize' }}
                />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  // Desktop table view
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id} sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} hover>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TransactionsTable;
